import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { apiService } from "../services/api";

// ============ TYPES ============

export interface FeatureUpdate {
  id: string;
  type: "new" | "improvement" | "upcoming" | "beta" | "fix";
  title: string;
  description: string;
  version?: string;
  date: string; // ISO date string
  image?: string;
  link?: string; // Screen ID or URL to navigate
  isExternal?: boolean;
  tags?: string[];
  isRead?: boolean;
}

interface WhatsNewContextType {
  updates: FeatureUpdate[];
  unreadCount: number;
  isLoading: boolean;
  isOpen: boolean;
  fetchUpdates: () => Promise<void>;
  markAsRead: (updateId: string) => void;
  markAllAsRead: () => void;
  toggleOpen: () => void;
  setOpen: (open: boolean) => void;
  setNavigationCallback: (callback: (target: string) => void) => void;
}

const WhatsNewContext = createContext<WhatsNewContextType | undefined>(undefined);

// ============ MOCK DATA FOR DEVELOPMENT ============

const mockUpdates: FeatureUpdate[] = [
  {
    id: "update-1",
    type: "new",
    title: "Card View for Data Tables",
    description: "Switch between Table and Card views on any data screen. Card view provides a visual overview with status badges, progress bars, and quick actions.",
    version: "2.1.0",
    date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    link: "103",
    tags: ["UI", "Data Tables"],
    isRead: false,
  },
  {
    id: "update-2",
    type: "new",
    title: "Interactive App Tour",
    description: "New users can now take a guided tour of the application. Access it anytime from your profile menu.",
    version: "2.1.0",
    date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    link: "start-tour",
    tags: ["Onboarding"],
    isRead: false,
  },
  {
    id: "update-3",
    type: "improvement",
    title: "Enhanced Workflow Progress",
    description: "Workflow progress bars now show color-coded stages. Completed stages appear in green, current in blue, and pending in gray.",
    version: "2.0.5",
    date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    link: "103",
    tags: ["Workflow", "UI"],
    isRead: false,
  },
  {
    id: "update-4",
    type: "upcoming",
    title: "Multi-Branch Reporting",
    description: "Coming soon: Generate consolidated reports across multiple branches with advanced filtering and export options.",
    date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
    tags: ["Reports", "Coming Soon"],
    isRead: true,
  },
  {
    id: "update-5",
    type: "beta",
    title: "AI-Powered Insights",
    description: "Try our new AI assistant that provides intelligent suggestions for inventory management and service scheduling.",
    version: "Beta",
    date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    tags: ["AI", "Beta"],
    isRead: true,
  },
  {
    id: "update-6",
    type: "fix",
    title: "Performance Improvements",
    description: "Fixed slow loading times on large data tables. Improved infinite scroll performance by 40%.",
    version: "2.0.4",
    date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    tags: ["Performance"],
    isRead: true,
  },
];

// ============ API & STORAGE CONFIGURATION ============

const WHATSNEW_ENDPOINT = "/api/whatsnew";
const WHATSNEW_STORAGE_KEY = "garage360-whatsnew-read";

// ============ HELPER FUNCTIONS ============

const getReadUpdates = (): string[] => {
  try {
    const stored = localStorage.getItem(WHATSNEW_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error("[WhatsNew] Error reading from localStorage:", error);
  }
  return [];
};

const saveReadUpdates = (readIds: string[]) => {
  try {
    localStorage.setItem(WHATSNEW_STORAGE_KEY, JSON.stringify(readIds));
  } catch (error) {
    console.error("[WhatsNew] Error saving to localStorage:", error);
  }
};

// ============ PROVIDER ============

export const WhatsNewProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [updates, setUpdates] = useState<FeatureUpdate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [navigationCallback, setNavigationCallbackState] = useState<((target: string) => void) | null>(null);

  // Calculate unread count
  const unreadCount = updates.filter((u) => !u.isRead).length;

  // Set navigation callback
  const setNavigationCallback = useCallback((callback: (target: string) => void) => {
    setNavigationCallbackState(() => callback);
  }, []);

  // Fetch updates from backend
  const fetchUpdates = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiService.get<FeatureUpdate[]>(WHATSNEW_ENDPOINT);
      if (response && Array.isArray(response)) {
        const readIds = getReadUpdates();
        const updatesWithReadState = response.map((u) => ({
          ...u,
          isRead: u.isRead || readIds.includes(u.id),
        }));
        setUpdates(updatesWithReadState);
        setIsLoading(false);
        return;
      }
    } catch (error) {
      console.log("[WhatsNew] Backend not available, using mock data");
    }

    // Fallback to mock data
    const readIds = getReadUpdates();
    const updatesWithReadState = mockUpdates.map((u) => ({
      ...u,
      isRead: u.isRead || readIds.includes(u.id),
    }));
    setUpdates(updatesWithReadState);
    setIsLoading(false);
  }, []);

  // Mark an update as read
  const markAsRead = useCallback((updateId: string) => {
    setUpdates((prev) =>
      prev.map((u) => (u.id === updateId ? { ...u, isRead: true } : u))
    );

    const readIds = getReadUpdates();
    if (!readIds.includes(updateId)) {
      saveReadUpdates([...readIds, updateId]);
    }
  }, []);

  // Mark all updates as read
  const markAllAsRead = useCallback(() => {
    setUpdates((prev) => prev.map((u) => ({ ...u, isRead: true })));
    const allIds = updates.map((u) => u.id);
    saveReadUpdates(allIds);
  }, [updates]);

  // Toggle dropdown open state
  const toggleOpen = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  // Set dropdown open state
  const setOpen = useCallback((open: boolean) => {
    setIsOpen(open);
  }, []);

  return (
    <WhatsNewContext.Provider
      value={{
        updates,
        unreadCount,
        isLoading,
        isOpen,
        fetchUpdates,
        markAsRead,
        markAllAsRead,
        toggleOpen,
        setOpen,
        setNavigationCallback,
      }}
    >
      {children}
    </WhatsNewContext.Provider>
  );
};

// ============ HOOK ============

export const useWhatsNew = (): WhatsNewContextType => {
  const context = useContext(WhatsNewContext);
  if (context === undefined) {
    throw new Error("useWhatsNew must be used within a WhatsNewProvider");
  }
  return context;
};
